import { createReactionEngine } from './labCatalog';
import { INDEXABLE_ELEMENT_SLUGS } from './indexableElementPages';
import PageIndexingPolicy from '../models/PageIndexingPolicy';
import { WORKSHEETS } from './worksheets';
import {
  buildElementPath,
  buildReactionPath,
  buildWorksheetAnswersPath,
  buildWorksheetPath,
  slugify,
} from '../utils/routeUtils';
import { ROUTES } from '../constants/config';

// US and other spellings people search for, shown on the element pages.
const ALTERNATE_NAMES = Object.freeze({ Al: 'Aluminum', Cs: 'Cesium', S: 'Sulphur' });

let sharedEngine = null;
const pageIndexingPolicy = new PageIndexingPolicy({ indexableElementSlugs: INDEXABLE_ELEMENT_SLUGS });

/**
 * Which pages search engines should index (see data/indexableElementPages.js).
 */
export function getPageIndexingPolicy() {
  return pageIndexingPolicy;
}

/**
 * One reaction engine shared by the content pages (the lab page keeps its own).
 */
export function getSiteEngine() {
  sharedEngine ||= createReactionEngine();
  return sharedEngine;
}

export function getElements() {
  return getSiteEngine()
    .getAllSubstances()
    .filter((substance) => substance.isElement());
}

export function getAlternateName(element) {
  return ALTERNATE_NAMES[element.id] ?? null;
}

export function findElementBySlug(slug) {
  return getElements().find((element) => slugify(element.name) === slug) ?? null;
}

/**
 * Period and group as printed on a periodic table. Lanthanides and actinides have no group.
 */
export function getElementPlace(element) {
  const { row, column } = element.tablePosition;
  if (row <= 7) return { period: row, group: column };
  return { period: row === 9 ? 6 : 7, group: null };
}

/**
 * Every famous reaction gets its own page: /reactions/potassium-and-water.
 */
export function getReactionPages() {
  const engine = getSiteEngine();
  return engine.getCuratedReactions().map((reaction) => {
    const [first, second] = reaction.reactantIds.map((id) => engine.getSubstance(id));
    return { reaction, first, second, slug: buildReactionPath(first, second).split('/').pop(), path: buildReactionPath(first, second) };
  });
}

export function findReactionPageBySlug(slug) {
  return getReactionPages().find((page) => page.slug === slug) ?? null;
}

/**
 * What an element does with every other substance, grouped for its page. Every partner lands in
 * exactly one group, so famous + compounds + mixtures + noReactionCount + tooRareCount = partnerCount.
 * The totals sort the famous ones by what they do too, so
 * newSubstanceCount + mixtureCount + nothingHappensCount = testedCount (every pair that can be tried).
 */
export function summarizeElementReactions(element) {
  const engine = getSiteEngine();
  const reactionPagesByKey = new Map(getReactionPages().map((page) => [page.reaction.getKey(), page]));
  const summary = { famous: [], compounds: [], mixtures: [], noReactionCount: 0, tooRareCount: 0 };
  const partners = engine.getAllSubstances().filter((partner) => partner.id !== element.id);

  partners.forEach((partner) => {
    const reaction = engine.mix(element.id, partner.id);
    const famousPage = reactionPagesByKey.get(reaction.getKey());
    if (famousPage) summary.famous.push({ partner, reaction, path: famousPage.path });
    else if (reaction.reactionClass === 'too-rare') summary.tooRareCount += 1;
    else if (!reaction.hasVisibleReaction()) summary.noReactionCount += 1;
    else if (reaction.isMixture) summary.mixtures.push({ partner, reaction });
    else summary.compounds.push({ partner, reaction });
  });

  const famousReactions = summary.famous.map(({ reaction }) => reaction);
  const famousMixtureCount = famousReactions.filter((reaction) => reaction.isMixture).length;
  const famousQuietCount = famousReactions.filter((reaction) => !reaction.hasVisibleReaction()).length;
  const partnerCount = partners.length;
  return {
    ...summary,
    partnerCount,
    testedCount: partnerCount - summary.tooRareCount,
    newSubstanceCount: summary.compounds.length + famousReactions.length - famousMixtureCount - famousQuietCount,
    mixtureCount: summary.mixtures.length + famousMixtureCount,
    nothingHappensCount: summary.noReactionCount + famousQuietCount,
    isTooRareToTest: summary.tooRareCount === partnerCount,
  };
}

/**
 * Every page address on the site, for the sitemap and for pre-rendering.
 */
export function getAllPagePaths() {
  return [
    ROUTES.LAB,
    ROUTES.ELEMENTS,
    ...getElements().map(buildElementPath),
    ROUTES.REACTIONS,
    ...getReactionPages().map((page) => page.path),
    ROUTES.PARENTS,
    ...WORKSHEETS.map(buildWorksheetPath),
    ...WORKSHEETS.filter((worksheet) => worksheet.hasAnswers).map(buildWorksheetAnswersPath),
    ROUTES.ABOUT,
    ROUTES.CONTACT,
    ROUTES.PRIVACY,
    ROUTES.TERMS,
  ];
}

/**
 * The page addresses search engines should index, for sitemap.xml. Thin pages are left out.
 */
export function getIndexablePagePaths() {
  return pageIndexingPolicy.filterIndexablePaths(getAllPagePaths());
}
